import { TableColumn } from './types';

/**
 * Returns the CSS class name for text alignment
 */
export const getAlignClass = (
  align: 'left' | 'center' | 'right' | undefined,
  styles: Record<string, string>
): string => {
  switch (align) {
    case 'center':
      return styles.alignCenter;
    case 'right':
      return styles.alignRight;
    default:
      return styles.alignLeft;
  }
};

/**
 * Builds inline styles for a column based on width, minWidth, maxWidth properties
 * Note: CSS flex doesn't work on table cells, so we only use width constraints
 */
export const getColumnStyle = <T>(column: TableColumn<T>): React.CSSProperties => {
  const style: React.CSSProperties = {};

  // Fixed width takes precedence
  if (column.width !== undefined) {
    style.width = typeof column.width === 'number' ? `${column.width}px` : column.width;
  }

  // Min/max width constraints
  if (column.minWidth !== undefined) {
    style.minWidth = `${column.minWidth}px`;
  }

  if (column.maxWidth !== undefined) {
    style.maxWidth = `${column.maxWidth}px`;
  }

  return style;
};

export const sortData = <T extends Record<string, unknown>>(
  data: T[],
  sortColumn: string | null,
  sortDirection: 'asc' | 'desc' | null
): T[] => {
  if (!sortColumn || !sortDirection) {
    return data;
  }

  return [...data].sort((a, b) => {
    const aValue = a[sortColumn];
    const bValue = b[sortColumn];

    // Handle null/undefined values
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;

    // Convert to string for comparison
    const aStr = String(aValue).toLowerCase();
    const bStr = String(bValue).toLowerCase();

    if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
    if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
};

export const paginateData = <T extends Record<string, unknown>>(
  data: T[],
  enabled: boolean,
  currentPage: number,
  pageSize: number
): T[] => {
  if (!enabled) {
    return data;
  }

  const startIndex = currentPage * pageSize;
  const endIndex = startIndex + pageSize;
  return data.slice(startIndex, endIndex);
};

export const convertToCSV = <T extends Record<string, unknown>>(
  data: T[],
  columns: TableColumn<T>[]
): string => {
  if (data.length === 0) return '';

  // Get all unique keys from the data objects
  const dataKeys = Object.keys(data[0] || {});

  // Try to use column titles for headers if column key matches data key
  // Otherwise use the actual data keys as headers
  const headers = dataKeys
    .map((key) => {
      const column = columns.find((col) => col.key === key);
      return column?.title || key;
    })
    .join(',');

  const rows = data.map((row) => {
    return dataKeys
      .map((key) => {
        const value = row[key];
        // Handle arrays and objects
        let stringValue: string;
        if (Array.isArray(value)) {
          stringValue = value.join('; ');
        } else if (typeof value === 'object' && value !== null) {
          stringValue = JSON.stringify(value);
        } else {
          stringValue = String(value ?? '');
        }
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      })
      .join(',');
  });

  return [headers, ...rows].join('\n');
};

export const downloadCSV = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
